import { useState, useEffect, useCallback } from "react";
import api, { API_BASE_URL } from "../../../utils/api";

// ---------------- helpers ----------------
const trimOrNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return null;
  return s;
};

// robust: strings, arrays, JSON strings, objects, "Home: xxx", delimiters
const pickFirst = (raw, type = "text") => {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    const v = raw.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
    return v || null;
  }

  let s = raw.toString().trim();
  if (!s) return null;

  const looksJson =
    (s.startsWith("[") && s.endsWith("]")) || (s.startsWith("{") && s.endsWith("}"));

  if (looksJson) {
    try {
      const parsed = JSON.parse(s);

      if (Array.isArray(parsed)) {
        const v = parsed.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
        return v || null;
      }

      if (parsed && typeof parsed === "object") {
        const possible =
          parsed.emails ||
          parsed.email ||
          parsed.emails_raw ||
          parsed.phone_numbers ||
          parsed.phones ||
          parsed.phone;

        if (Array.isArray(possible)) {
          const v = possible.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
          return v || null;
        }

        if (typeof possible === "string") s = possible.trim();
      }
    } catch {
      // ignore
    }
  }

  s = s.replace(/[\[\]"]/g, "").trim();
  const parts = s.split(/[;,|\n|]/).map((x) => x.trim()).filter(Boolean);
  if (!parts.length) return null;

  if (type === "email") {
    const found = parts.find((p) => p.includes("@"));
    return found || parts[0] || null;
  }

  if (type === "phone") {
    const first = parts[0].replace(/^[a-zA-Z\s]+:\s*/g, "").trim();
    const cleaned = first.replace(/[^\d+()\-\s]/g, "").trim();
    return cleaned || first || null;
  }

  return parts[0] || null;
};

const buildMemberFromProperty = (p, fallbackCode) => {
  const read = (...keys) => {
    for (const k of keys) {
      const v = p?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return null;
  };

  const accountId =
    read("account_id", "Account_ID", "AccountId", "accountId") ??
    fallbackCode ??
    read("id", "ID") ??
    "";

  const mailingName = read("mailing_name", "Mailing_Name", "mailingName") || "";

  const rawEmails = read("emails", "Emails", "EMAILS", "email", "Email");
  const rawPhones = read(
    "phone_numbers",
    "Phone_Numbers",
    "PHONE_NUMBERS",
    "phoneNumbers",
    "phones",
    "Phones"
  );

  const fullAddress =
    [
      read("street_1", "Street_1"),
    
    ]
      .map((x) => (x || "").toString().trim())
      .filter(Boolean)
      .join(", ") || null;

  const mailingAddress =
    [
      read("mailing_street_1", "Mailing_Street_1"),
   
    ]
      .map((x) => (x || "").toString().trim())
      .filter(Boolean)
      .join(", ") || null;

  const displayName = mailingName.toString().trim() || `Property ${accountId}`.trim();
  const parts = displayName.split(/\s+/).filter(Boolean);
  const first = parts[0] || "Property";
  const last = parts.length > 1 ? parts.slice(1).join(" ") : "";

  return {
    ...p,

    // fields your MemberProfileCard expects
    ClubMemberCode: String(accountId),
    account_id: String(accountId),

    FirstName: first,
    LastName: last,
    FullName: displayName,
    fullname: displayName,

    EmailAddress: pickFirst(rawEmails, "email"),
    PhoneNumber: pickFirst(rawPhones, "phone"),
    AlternatePhoneNumber: null,

    FullAddress: fullAddress,
    AlternateAddress: mailingAddress,

    JoinDate: null,
    Gender: null,
    ClubStatusRuleCode: null,
    PhotoGraphicFileName: null,

    isPropertyFallback: true,
    member_source: "property",
  };
};

// ---------------- hook ----------------
const useWorkOrderDetails = (workOrderId, currentUserId) => {
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [member, setMember] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [priorityLevels, setPriorityLevels] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [workOrderSubtypes, setWorkOrderSubtypes] = useState([]);
  const [workOrderLocations, setWorkOrderLocations] = useState([]);
  const [comments, setComments] = useState([]);
  const [imageURL, setImageURL] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentsError, setCommentsError] = useState(null);
  const [filesError, setFilesError] = useState(null);
  const [mutationError, setMutationError] = useState(null);

  const fetchComments = async () => {
    try {
      setCommentsError(null);
      const res = await api.get(`/getComments/${workOrderId}`);
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
      setCommentsError("Failed to load comments. Please try again.");
    }
  };

  const fetchAttachedFiles = async () => {
    try {
      setFilesError(null);
      const res = await api.get(`/workorder-files/${workOrderId}`);
      setAttachedFiles(res.data);

      const firstImageFile = res.data.find(
        (file) => file.file_type && file.file_type.startsWith("image/")
      );

      if (firstImageFile) {
        const imageUrl = `${API_BASE_URL}/workorderimages/${encodeURIComponent(
          firstImageFile.file_url.split(/(\\|\/)/g).pop()
        )}`;
        setImageURL(imageUrl);
      } else {
        setImageURL(null);
      }
    } catch (err) {
      console.error("Failed to fetch attached files:", err);
      setFilesError("Failed to load attached files. Please try again.");
    }
  };

  const fetchDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const workOrderResp = await api.get(`/getWorkOrders/${workOrderId}`);
      const workOrderData = workOrderResp.data;

      // ✅ ORIGINAL behavior: try member API first
      // ✅ NEW behavior: only if member is 404, then fallback to property API
      const propertyId = trimOrNull(workOrderData?.property_id);

      let memberData = null;

      if (propertyId) {
        try {
          const memberResp = await api.get(`/members/${encodeURIComponent(propertyId)}`);
          memberData = memberResp.data;
        } catch (e) {
          const status = e?.response?.status;

          // Only fallback on 404
          if (status === 404) {
            try {
              const propResp = await api.get(`/properties/${encodeURIComponent(propertyId)}`);
              memberData = buildMemberFromProperty(propResp.data, propertyId);
            } catch (pe) {
              console.error("Property fallback failed:", pe);
              memberData = null;
            }
          } else {
            // other errors should not silently become property fallback
            console.error("Member fetch failed (non-404):", e);
            throw e;
          }
        }
      }

      const [
        eventsResp,
        assignableUsersResp,
        priorityResp,
        usersResp,
        statusesResp,
        workOrderTypesResp,
        subtypesResp,
        locationsResp,
      ] = await Promise.all([
        api.get(`/getEvents?work_order_id=${workOrderId}`),
        api.get(`/users-by-role`),
        api.get(`/priority-levels`),
        api.get(`/users`),
        api.get(`/statuses/workorders`),
        api.get(`/workorder-types`),
        api.get(`/workorder-subtypes?type_id=${workOrderData.workorder_type_id}`),
        api.get(`/workorder-locations?type_id=${workOrderData.workorder_type_id}`),
      ]);

      setWorkOrderDetails(workOrderData);
      setMember(memberData);

      setEvents(
        (eventsResp.data || []).map((event) => ({
          ...event,
          start: new Date(event.start_time),
          end: new Date(event.end_time),
        }))
      );

      setAssignableUsers(assignableUsersResp.data);
      setPriorityLevels(priorityResp.data);
      setUsers(usersResp.data);
      setStatuses(statusesResp.data);
      setWorkOrderTypes(workOrderTypesResp.data);
      setWorkOrderSubtypes(subtypesResp.data);
      setWorkOrderLocations(locationsResp.data);

      setSelectedAssignee(workOrderData.assign_to || null);
      setSelectedPriority(workOrderData.priority || "");
      setSelectedTypeId(workOrderData.workorder_type_id || "");
      setSelectedSubtypeId(workOrderData.workorder_subtype_id || "");
      setSelectedLocationId(workOrderData.location_id || "");
    } catch (err) {
      console.error("Failed to fetch work order details:", err);
      setError("Failed to fetch details.");
    } finally {
      setIsLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchDetails();
    fetchComments();
    fetchAttachedFiles();
  }, [fetchDetails]);

  // handlers unchanged...
  const handleTypeChange = async (event) => {
    const newTypeId = event.target.value;
    try {
      const [subtypesRes, locationsRes] = await Promise.all([
        api.get(`/workorder-subtypes-by-type?type_id=${newTypeId}`),
        api.get(`/workorder-locations-by-type?type_id=${newTypeId}`),
      ]);

      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        workorder_type_id: newTypeId,
        status: workOrderDetails.status,
        assign_to: workOrderDetails.assign_to,
        priority: workOrderDetails.priority,
        workorder_subtype_id: null,
        location_id: null,
      });

      setWorkOrderSubtypes(subtypesRes.data);
      setWorkOrderLocations(locationsRes.data);
      setSelectedTypeId(newTypeId);
      setSelectedSubtypeId(null);
      setSelectedLocationId(null);
      setWorkOrderDetails((prev) => ({
        ...prev,
        workorder_type_id: newTypeId,
        workorder_subtype_id: null,
        location_id: null,
      }));
    } catch (error) {
      console.error("Failed to update type:", error);
      setMutationError("Failed to update work order. Please try again.");
    }
  };

  const handleSubtypeChange = async (event) => {
    const subtypeId = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        workorder_subtype_id: subtypeId,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedSubtypeId(subtypeId);
    } catch (err) {
      console.error(err);
      setMutationError("Failed to update work order. Please try again.");
    }
  };

  const handleLocationChange = async (event) => {
    const locationId = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        location_id: locationId,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedLocationId(locationId);
    } catch (err) {
      console.error(err);
      setMutationError("Failed to update work order. Please try again.");
    }
  };

  const handleAssigneeChange = async (event) => {
    const newAssignee = event.target.value;
    try {
      await api.put(`/assignWorkOrder`, {
        order_id: workOrderDetails.order_id,
        assign_to: newAssignee,
      });
      setSelectedAssignee(newAssignee);
      setWorkOrderDetails((prev) => ({ ...prev, assign_to: newAssignee }));
    } catch (error) {
      console.error("Error assigning user:", error);
      setMutationError("Failed to update work order. Please try again.");
    }
  };

  const handlePriorityChange = async (event) => {
    const newPriority = event.target.value;
    try {
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
        priority: newPriority,
        assign_to: workOrderDetails.assign_to,
        status: workOrderDetails.status,
      });
      setSelectedPriority(newPriority);
      setWorkOrderDetails((prev) => ({ ...prev, priority: newPriority }));
    } catch (error) {
      console.error("Failed to update priority:", error);
      setMutationError("Failed to update work order. Please try again.");
    }
  };

  const calculateDaysOpen = (createdAt) => {
    if (!createdAt) return "N/A";
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now - createdDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? "Open Today" : `Open for ${diffDays} day(s)`;
  };

  return {
    workOrderDetails,
    setWorkOrderDetails,
    member,
    events,
    users,
    statuses,
    assignableUsers,
    priorityLevels,
    workOrderTypes,
    workOrderSubtypes,
    workOrderLocations,
    selectedAssignee,
    selectedPriority,
    selectedTypeId,
    selectedSubtypeId,
    selectedLocationId,
    isLoading,
    error,
    commentsError,
    filesError,
    mutationError,
    imageURL,
    attachedFiles,
    fetchDetails,
    fetchComments,
    comments,
    setComments,
    setEvents,
    setUsers,
    handleTypeChange,
    handleSubtypeChange,
    handleLocationChange,
    handleAssigneeChange,
    handlePriorityChange,
    calculateDaysOpen,
  };
};

export default useWorkOrderDetails;
